<template>
    <div class="pdf-viewer-container">
        <!-- PDF Viewer Header - Hidden on Mobile to avoid duplication -->
        <div class="hidden sm:flex sm:items-center sm:justify-between mb-4 p-4 bg-white/10 backdrop-blur-xl rounded-lg border border-white/20">
            <div class="flex items-center space-x-4">
                <h3 class="text-lg font-semibold text-white">{{ title || 'PDF Document' }}</h3>
                <span v-if="isLoading" class="text-sm text-white/60">Loading...</span>
            </div>
            <div class="flex items-center space-x-2">
                <!-- Download Button -->
                <a
                    :href="pdfUrl"
                    download
                    class="inline-flex items-center justify-center bg-green-500 hover:bg-green-600 text-white px-3 py-2 rounded-lg transition-colors text-sm"
                >
                    <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
                    </svg>
                    Download
                </a>
                
                <!-- Open in New Tab Button -->
                <a
                    :href="pdfUrl"
                    target="_blank"
                    class="inline-flex items-center justify-center bg-auas-500 hover:bg-auas-600 text-white px-3 py-2 rounded-lg transition-colors text-sm"
                >
                    <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"></path>
                    </svg>
                    Open in New Tab
                </a>
            </div>
        </div>

        <!-- PDF Embed Container - Mobile Responsive -->
        <div class="pdf-embed-container bg-white/10 backdrop-blur-xl rounded-lg border border-white/20 overflow-hidden">
            <div v-if="isLoading" class="flex items-center justify-center h-64 sm:h-96">
                <div class="text-center">
                    <div class="inline-flex items-center justify-center w-16 h-16 bg-white/10 backdrop-blur-xl rounded-2xl mb-4 border border-white/20">
                        <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-white"></div>
                    </div>
                    <p class="text-white font-medium">Loading PDF...</p>
                    <p class="text-white/60 text-sm mt-1">Please wait while we load the document</p>
                </div>
            </div>
            
            <div v-else-if="error" class="flex items-center justify-center h-64 sm:h-96">
                <div class="text-center">
                    <div class="inline-flex items-center justify-center w-16 h-16 bg-red-500/20 rounded-2xl mb-4 border border-red-500/30">
                        <svg class="w-8 h-8 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                        </svg>
                    </div>
                    <h3 class="text-lg font-medium text-white mb-2">Error Loading PDF</h3>
                    <p class="text-white/60 mb-4">{{ error }}</p>
                    <button
                        @click="retryLoad"
                        class="bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded-lg transition-colors"
                    >
                        Try Again
                    </button>
                </div>
            </div>
            
            <div v-else class="pdf-viewer">
                <!-- Embedded PDF with mobile-responsive container -->
                <div class="relative w-full overflow-hidden rounded-lg">
                    <iframe
                        :src="pdfUrl + '#toolbar=1&navpanes=1&scrollbar=1&view=FitH'"
                        class="w-full border-0"
                        :style="{ height: height }"
                        frameborder="0"
                        @load="onPdfLoad"
                        @error="onPdfError"
                    />
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'

const props = defineProps({
    pdfUrl: {
        type: String,
        required: true
    },
    title: {
        type: String,
        default: ''
    },
    height: {
        type: String,
        default: '600px'
    }
})

const emit = defineEmits(['loaded', 'error', 'mount-error'])

const isLoading = ref(true)
const error = ref(null)

const onPdfLoad = () => {
    console.log('PDF iframe loaded successfully')
    isLoading.value = false
    error.value = null
    emit('loaded')
}

const onPdfError = () => {
    console.error('PDF iframe failed to load')
    isLoading.value = false
    error.value = 'Failed to load PDF document. Please try downloading it instead.'
    emit('error', 'Failed to load PDF document')
}

const retryLoad = () => {
    isLoading.value = true
    error.value = null
    // Force iframe reload by changing src
    const iframe = document.querySelector('.pdf-viewer iframe')
    if (iframe) {
        const currentSrc = iframe.src
        iframe.src = ''
        setTimeout(() => {
            iframe.src = currentSrc
        }, 100)
    }
}

onMounted(() => {
    console.log('PdfViewer mounted, PDF URL:', props.pdfUrl)
    
    // Check if PDF URL is provided
    if (!props.pdfUrl) {
        console.error('No PDF URL provided')
        emit('error', 'No PDF URL provided')
        return
    }
    
    // Try to load the PDF in iframe
    console.log('Attempting to load PDF in iframe')
    
    // For PDFs, we'll assume they load successfully after a short delay
    // since the @load event often doesn't fire for PDFs
    setTimeout(() => {
        console.log('PDF iframe should be loaded by now, assuming success')
        if (isLoading.value) {
            isLoading.value = false
            emit('loaded')
        }
    }, 2000) // Give it 2 seconds to load
    
    // Set a longer timeout as fallback
    setTimeout(() => {
        if (isLoading.value) {
            console.warn('PDF loading timeout in PdfViewer component')
            isLoading.value = false
            error.value = 'PDF loading timeout'
            emit('error', 'PDF loading timeout')
        }
    }, 8000) // 8 second timeout
})
</script>

<style scoped>
.pdf-viewer-container {
    max-width: 100%;
}

.pdf-embed-container {
    min-height: 400px;
}

@media (min-width: 640px) {
    .pdf-embed-container {
        min-height: 600px;
    }
}

.pdf-viewer iframe {
    border: none;
    background: white;
    border-radius: 0.5rem;
}

/* Loading animation */
@keyframes spin {
    to {
        transform: rotate(360deg);
    }
}

.animate-spin {
    animation: spin 1s linear infinite;
}

/* Mobile-specific improvements */
@media (max-width: 640px) {
    .pdf-viewer-container {
        margin: 0 -1rem;
    }
    
    .pdf-embed-container {
        border-radius: 0;
        margin: 0;
    }
    
    .pdf-viewer iframe {
        border-radius: 0;
        min-height: 400px;
    }
}
</style>